import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";

import { useDictionariesOptions } from "api/hooks";
import { CourseFromGroupApi } from "api";
import { COURSE_FORM_FIELD_TYPES } from "consts";
import {
  PaziaButton,
  PaziaCheckbox,
  PaziaFormButton,
  PaziaInput,
  PaziaTextarea,
} from "components/atoms";

interface ApplicationFormProps {
  onSubmit: (data: ApplicationFormSchema) => Promise<void>;
  group: CourseFromGroupApi | null;
  onCancel: () => void;
}
export const ApplicationForm = ({ onSubmit, onCancel, group }: ApplicationFormProps) => {
  const { loading: loadingDictionaries, options: dictionariesOptions } = useDictionariesOptions();
  const methods = useForm<ApplicationFormSchema>({
    resolver: yupResolver(applicationFormSchema),
    defaultValues: {
      [APPLICATION_FORM_NAMES.ID]: group?.courseFormGroupId,
      [APPLICATION_FORM_NAMES.NAME]: group?.name,
      [APPLICATION_FORM_NAMES.IS_TEMPLATE]: group?.isTemplate,
      [APPLICATION_FORM_NAMES.DESCRIPTION]: group?.description,
    },
  });

  const { handleSubmit } = methods;

  if (loadingDictionaries || !dictionariesOptions) return null;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
        <PaziaInput name={APPLICATION_FORM_NAMES.NAME} label="Nazwa grupy" className="w-full" required />
        <PaziaTextarea name={APPLICATION_FORM_NAMES.DESCRIPTION} label="Opis" />
        <PaziaCheckbox name={APPLICATION_FORM_NAMES.IS_TEMPLATE} label="Zapisz jako szablon" />
        <div className="flex justify-end gap-2">
          <PaziaButton title="Anuluj" type="tertiary" btnType="button" onClick={onCancel} />
          <PaziaFormButton title="Zapisz" type="success" />
        </div>
      </form>
    </FormProvider>
  );
};

export const APPLICATION_FORM_NAMES = {
  ID: "id",
  NAME: "name",
  IS_TEMPLATE: "isTemplate",
  IS_REQUIRED: "isRequired",
  DESCRIPTION: "description",
  FIELDS: "fields",
  FIELDS_ID: "id",
  FIELDS_NAME: "name",
  FIELDS_DESCRIPTION: "description",
  FIELDS_FIELDTYPE: "fieldType",
  FIELDS_DICTIONARY: "dictionary",
  FIELDS_CONDITIONS: "conditions",
  CONDITIONS_FIELD: "fieldId",
  CONDITIONS_CONDITION: "condition",
  CONDITIONS_CONDITION_VALUE: "conditionValue",
  CONDITIONS_FIELD_TYPE: "fieldType",
  CONDITIONS_FIELD_OPTIONS: "options",
} as const;

export const formFieldConditionSchema = yup.object({
  [APPLICATION_FORM_NAMES.CONDITIONS_FIELD]: yup.number().required(),
  [APPLICATION_FORM_NAMES.CONDITIONS_CONDITION]: yup.number().required(),
  [APPLICATION_FORM_NAMES.CONDITIONS_CONDITION_VALUE]: yup.mixed().required(),
  [APPLICATION_FORM_NAMES.CONDITIONS_FIELD_TYPE]: yup.number().required(),
});

export const formFieldSchema = yup.object().shape({
  [APPLICATION_FORM_NAMES.FIELDS_ID]: yup.number(),
  [APPLICATION_FORM_NAMES.FIELDS_NAME]: yup.string().required(),
  [APPLICATION_FORM_NAMES.FIELDS_DESCRIPTION]: yup.string(),
  [APPLICATION_FORM_NAMES.FIELDS_FIELDTYPE]: yup.number().required(),
  [APPLICATION_FORM_NAMES.IS_REQUIRED]: yup.boolean(),
  [APPLICATION_FORM_NAMES.FIELDS_DICTIONARY]: yup
    .number()
    .nullable()
    .when(APPLICATION_FORM_NAMES.FIELDS_FIELDTYPE, {
      is: COURSE_FORM_FIELD_TYPES.DICTIONARY,
      then: (shema) => shema.required(),
    }),
  [APPLICATION_FORM_NAMES.FIELDS_CONDITIONS]: yup.array().of(formFieldConditionSchema),
});

export const applicationFormSchema = yup
  .object({
    [APPLICATION_FORM_NAMES.ID]: yup.number(),
    [APPLICATION_FORM_NAMES.NAME]: yup.string().required(),
    [APPLICATION_FORM_NAMES.IS_TEMPLATE]: yup.boolean(),
    [APPLICATION_FORM_NAMES.DESCRIPTION]: yup.string(),
  })
  .required();

export type FormFieldSchema = yup.InferType<typeof formFieldSchema>;
export type FormFieldConditionSchema = yup.InferType<typeof formFieldConditionSchema>;
export type ApplicationFormSchema = yup.InferType<typeof applicationFormSchema>;
